// Default React import has to be here for the play test to run
import React, { useEffect, useRef, useMemo, useState } from 'react';
import { Controls, Player, PlayerEvent } from '@lottiefiles/react-lottie-player';
import { useIntersectionObserver, useMediaQuery } from 'usehooks-ts';
import uniqueId from 'lodash/uniqueId';
import classNames from 'classnames';

import { ImageEmbedded } from '../image/image-embedded';

import { LottieAnimationProps, LottieAnimationType } from './lottie-animation.config';

const LottieAnimation = ({
  additionalClasses,
  lottieId,
  assetPath,
  fallbackImage,
  autoloop,
  type,
  isInEditor,
}: LottieAnimationProps) => {
  const [showFallback, setShowFallback] = useState(isInEditor || false);
  const ref = useRef<Player>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const entry = useIntersectionObserver(containerRef, {});
  const isVisible = entry?.isIntersecting;
  const preferReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const isAutoPlay = useMemo(
    () => type === LottieAnimationType.ON_PAGE_LOAD && !preferReducedMotion,
    [type, preferReducedMotion]
  );
  const isAutoLoop = useMemo(
    () => autoloop && !preferReducedMotion && !isInEditor,
    [autoloop, isInEditor, preferReducedMotion]
  );
  const controls = useMemo(
    () => type === LottieAnimationType.ON_CLICK || preferReducedMotion,
    [type, preferReducedMotion]
  );
  const isHover = useMemo(
    () => type === LottieAnimationType.ON_HOVER && !preferReducedMotion,
    [type, preferReducedMotion]
  );

  // Needed for correct author side behavior
  const lottieKey = useMemo(
    () => uniqueId('lottie'),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetPath, autoloop]
  );

  const onEvent = (event: PlayerEvent) => {
    if (event === PlayerEvent.Load) {
      setShowFallback(false);
    }
    if (event === PlayerEvent.Error) {
      setShowFallback(true);
    }
  };

  useEffect(() => {
    if (isVisible && type === LottieAnimationType.ON_VIEWPORT_ENTRY && !preferReducedMotion) {
      ref?.current?.play();
    }
  }, [isVisible, preferReducedMotion, type]);

  return (
    <div
      ref={showFallback ? null : containerRef}
      key={lottieKey}
      data-testid="snowflake-lottie-animation"
      className={classNames('snowflake-lottie-animation', additionalClasses, {
        'snowflake-lottie-animation-hidden': showFallback,
      })}
    >
      {!showFallback && (
        <Player
          ref={ref}
          hover={isHover}
          loop={isAutoLoop}
          autoplay={isAutoPlay}
          id={lottieId}
          onEvent={onEvent}
          src={assetPath || ''}
          keepLastFrame
        >
          <Controls visible={controls} buttons={['play', 'repeat', 'stop']} />
        </Player>
      )}
      {showFallback && (
        <ImageEmbedded
          dataTestID="snowflake-lottie-animation-fallback"
          baseCssClass="snowflake-lottie-animation-fallback"
          src={fallbackImage || ''}
          lazyEnabled
        />
      )}
    </div>
  );
};

export default LottieAnimation;
